import type { Joint } from "@/shared/state/visualization/schema/v1/three-d";

import { jointMotion } from "./jointPosing";

/**
 * One unit a joint's published values may be in, and what it converts to.
 *
 * `scale` is a multiplier into the extension's SI unit for that joint's motion
 * — radians for a joint that turns, metres for one that slides — which is
 * exactly what {@link JointBinding.scale} carries. There is no offset here for
 * the same reason there is none there: a consumer only applies the number.
 */
export interface JointUnitOption {
  /** How the unit is written: the picker's label, and the row note's spelling. */
  unit: string;
  /** Multiplier into the SI unit of this joint's motion. */
  scale: number;
  /**
   * Spellings a field catalogue may use for this unit, lower-cased.
   *
   * The catalogue is written by whoever ingested the recording, so `Degrees`
   * and `millimeters` turn up as often as `deg` and `mm`. Listed exhaustively
   * rather than matched by prefix: `metres` and `millimetres` differ by two
   * characters and by a factor of a thousand.
   */
  aliases: readonly string[];
}

/**
 * The units a joint that **slides** may be published in.
 *
 * Ordered coarse to fine, which is also most to least likely: metres are the
 * extension's own unit, and micrometres are here for the encoder counts a
 * linear stage occasionally reports.
 */
export const DISPLACEMENT_UNITS: readonly JointUnitOption[] = [
  { unit: "m", scale: 1, aliases: ["m", "metre", "metres", "meter", "meters"] },
  {
    unit: "cm",
    scale: 0.01,
    aliases: ["cm", "centimetre", "centimetres", "centimeter", "centimeters"],
  },
  {
    unit: "mm",
    scale: 0.001,
    aliases: ["mm", "millimetre", "millimetres", "millimeter", "millimeters"],
  },
  {
    unit: "µm",
    scale: 1e-6,
    aliases: [
      // Both the micro sign (U+00B5) and Greek mu (U+03BC) reach a catalogue,
      // and they are different characters that render identically.
      "µm",
      "μm",
      "um",
      "micrometre",
      "micrometres",
      "micrometer",
      "micrometers",
      "micron",
      "microns",
    ],
  },
];

/**
 * The units a joint that **turns** may be published in.
 *
 * Degrees first because a value that needs converting at all is nearly always
 * in them; radians are what the extension already assumes.
 */
export const ROTATION_UNITS: readonly JointUnitOption[] = [
  {
    unit: "deg",
    scale: Math.PI / 180,
    aliases: ["deg", "degree", "degrees"],
  },
  { unit: "rad", scale: 1, aliases: ["rad", "radian", "radians"] },
];

/**
 * What this joint's data may be published in, decided by the joint's own type.
 *
 * The joint is the authority, not the field: a `prismatic` joint's value is a
 * displacement whatever a topic calls it, so `deg` is not a unit it can be
 * published in and is never offered. Offering the wrong family would let a user
 * declare a "conversion" that converts nothing — the picker would read as an
 * answer while the robot moved by an arbitrary factor.
 */
export function unitOptionsFor(joint: Joint): readonly JointUnitOption[] {
  return jointMotion(joint) === "slide" ? DISPLACEMENT_UNITS : ROTATION_UNITS;
}

/** The option that needs no conversion — metres, or radians. */
function siOptionOf(options: readonly JointUnitOption[]): JointUnitOption {
  // Every set above contains exactly one, by construction.
  return options.find((option) => option.scale === 1) as JointUnitOption;
}

/** The option a written unit names, if this joint's set contains one. */
function optionNamed(
  options: readonly JointUnitOption[],
  unit: string | undefined,
): JointUnitOption | undefined {
  if (unit === undefined) {
    return undefined;
  }
  const normalized = unit.trim().toLowerCase();
  return options.find((option) => option.aliases.includes(normalized));
}

/**
 * How closely a stored `scale` must match an option's for that option to be the
 * unit it was written in.
 *
 * Relative, and deliberately tight. A factor this UI wrote round-trips through
 * JSON exactly, so the tolerance is only for a producer that stored π/180
 * truncated — not for deciding that some nearby number is "roughly degrees".
 * The families are orders of magnitude apart (`deg` to `rad` is 57×, `mm` to
 * `cm` is 10×), so nothing is at risk of matching the wrong one; what a loose
 * tolerance would risk is claiming a hand-written factor is a unit it is not.
 */
const SCALE_TOLERANCE = 1e-9;

/** The option whose conversion *is* this scale, if this joint's set has one. */
function optionScaling(
  options: readonly JointUnitOption[],
  scale: number | undefined,
): JointUnitOption | undefined {
  if (scale === undefined || !Number.isFinite(scale)) {
    return undefined;
  }
  return options.find(
    (option) =>
      Math.abs(option.scale - scale) <=
      SCALE_TOLERANCE * Math.abs(option.scale),
  );
}

/** Where the unit a row is applying came from. */
export type UnitSource =
  /** The user picked it on this row. */
  | "override"
  /** The binding already stored declares a scale, and this is the unit it is. */
  | "binding"
  /** The field catalogue declares it, and nothing more specific overrode it. */
  | "catalogue"
  /** Nothing usable said, so the extension's own SI unit stands. */
  | "assumed";

export interface ResolvedUnit {
  /** Every unit this joint may be published in — the picker's options. */
  options: readonly JointUnitOption[];
  /** The one in force, whose `scale` the binding is written with. */
  selected: JointUnitOption;
  source: UnitSource;
}

/**
 * The unit a joint's binding is written with, and where that came from.
 *
 * Precedence is **the override, then the scale already stored, then the
 * catalogue, then the SI assumption** — most specific to least, and the
 * parameter order does not spell it. The catalogue sits last of the three
 * because it is the one nobody chose: it is routinely absent at ingest time (a
 * URDF is normally ingested before the robot has ever been driven) and
 * routinely wrong when present.
 *
 * ## Why the stored scale is read back at all
 *
 * A binding's `scale` is the conversion *in force right now*, so a row that
 * ignored it would show one unit while applying another — and, worse, could not
 * undo it: a stored π/180 displayed as `rad` leaves the user selecting `rad`
 * over `rad`, which is no change, emits nothing, and saves the same degrees
 * factor back. The joint goes on moving 57 times too little, and the control
 * that exists to fix it looks like it does nothing. Reading it back makes the
 * picker show `deg`, so choosing `rad` is a real change that reaches the file.
 *
 * **One value decides both what is displayed and what is applied.** The picker
 * shows `selected.unit` and the binding is written with `selected.scale`, so
 * there is no way for the row to say `deg` while sending a metric factor. A
 * displayed unit and an applied number derived separately is precisely how a
 * control ends up lying about what it saved.
 *
 * Anything unrecognised — a unit from the wrong family, a spelling nothing
 * lists, a hand-written factor no offered unit accounts for — is **dropped
 * rather than guessed**, and the row falls through to the next source. An
 * unscaled binding is wrong by a factor a user can see and correct; a wrongly
 * scaled one is wrong by a factor nothing records. That is the same trade
 * `scaleForUnit` already makes. The consequence for a factor like `0.5` is that
 * the picker cannot name it: the row's note still reports the raw number, and
 * picking any unit replaces it outright.
 *
 * @param joint the joint being bound; its type decides which units exist
 * @param override the unit picked on this row, if any
 * @param catalogueUnit the unit the field catalogue declares for the bound field
 * @param declaredScale the `scale` the stored binding carries, if it carries one
 */
export function resolveUnit(
  joint: Joint,
  override: string | undefined,
  catalogueUnit: string | undefined,
  declaredScale?: number,
): ResolvedUnit {
  const options = unitOptionsFor(joint);
  const chosen = optionNamed(options, override);
  if (chosen !== undefined) {
    return { options, selected: chosen, source: "override" };
  }
  const stored = optionScaling(options, declaredScale);
  if (stored !== undefined) {
    return { options, selected: stored, source: "binding" };
  }
  const declared = optionNamed(options, catalogueUnit);
  if (declared !== undefined) {
    return { options, selected: declared, source: "catalogue" };
  }
  return { options, selected: siOptionOf(options), source: "assumed" };
}
